import { useRef, useState, useCallback } from 'react'
import { useAuthStore } from '@/store/auth.store'
import { useUploadAvatar } from '../hooks/useProfile'
import { Alert } from '@/components/ui/Alert'
import { useTranslation } from 'react-i18next'

const MAX_SIZE_BYTES = 5 * 1024 * 1024 // 5 MB
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp']

export function AvatarUpload() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const { mutate: upload, isPending } = useUploadAvatar()
  const inputRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const processFile = useCallback(
    (file: File) => {
      setFileError(null)
      if (!ACCEPTED.includes(file.type)) {
        setFileError(t('profile.avatar_invalid_format'))
        return
      }
      if (file.size > MAX_SIZE_BYTES) {
        setFileError(t('profile.avatar_too_large'))
        return
      }
      const url = URL.createObjectURL(file)
      setPreview(url)
      upload(file)
    },
    [upload, t]
  )

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) processFile(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) processFile(file)
  }

  const avatarSrc = preview ?? user?.avatar_url

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-base font-semibold text-neutral-900">{t('profile.avatar_title')}</h2>

      <div className="flex items-center gap-5">
        <div className="relative h-20 w-20 flex-shrink-0">
          {avatarSrc ? (
            <img
              src={avatarSrc}
              alt={t('profile.avatar_alt')}
              className="h-20 w-20 rounded-full object-cover border-2 border-surface-200"
            />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-mindeia-primary-light border-2 border-mindeia-primary-light text-mindeia-primary text-2xl font-bold">
              {user?.first_name?.[0]?.toUpperCase() ?? '?'}
            </div>
          )}
          {isPending && (
            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
              <span className="text-white text-xs">...</span>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          aria-label={t('profile.avatar_change')}
          className={[
            'flex min-h-[44px] flex-1 flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-5 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary',
            isDragging
              ? 'border-mindeia-primary bg-mindeia-primary-subtle'
              : 'border-surface-200 bg-surface-50 hover:border-mindeia-primary hover:bg-mindeia-primary-subtle',
          ].join(' ')}
        >
          <span className="text-sm font-medium text-neutral-700">{t('profile.avatar_drop')}</span>
          <span className="text-xs text-neutral-600 mt-0.5">{t('profile.avatar_constraints')}</span>
        </button>

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED.join(',')}
          onChange={handleFileChange}
          className="sr-only"
          aria-hidden="true"
        />
      </div>

      {fileError && <Alert variant="error">{fileError}</Alert>}
    </div>
  )
}
